import { useEffect, useRef } from 'react';
import { useAuthFrontLock } from '../context/useAuthFrontContext';
import loadHotjarScript from '../utils/utils/loadHotjarScript';

let isHotjarLoaded: boolean = false;

// Export a reset function for testing
export const resetHotjarState = () => {
  isHotjarLoaded = false;
  // Remove any existing Hotjar script
  const existingScript = document.getElementById('hotjar-script');
  if (existingScript) {
    existingScript.remove();
  }
};

// Extract helper function to check if Hotjar should load
const shouldLoadHotjar = (authFrontLock: any): boolean => {
  const additionalParams = authFrontLock?.getAdditionalParams();
  const isHotjarConsentPresent = additionalParams?.hotjarEnabled === true;
  
  return !isHotjarLoaded && 
         process.env.REACT_APP_ENABLE_HOTJAR === 'true' && 
         isHotjarConsentPresent;
};

// Extract Hotjar loading logic
const loadHotjar = (authFrontLock: any) => {
  if (!shouldLoadHotjar(authFrontLock)) {
    return;
  }
  
  loadHotjarScript();
  isHotjarLoaded = true;
};

// Extract helper to create event cleanup function
const createEventCleanup = (element: Element | Window, eventType: string, listener: EventListener): (() => void) => {
  return () => element.removeEventListener(eventType, listener);
};

const useHotjar = () => {
  const authFrontLock = useAuthFrontLock();
  const listenersRef = useRef<(() => void)[]>([]);

  useEffect(() => {
    // Create event listener that loads Hotjar
    const hotjarEventListener = () => loadHotjar(authFrontLock);

    // Setup body event listeners
    const setupBodyEventListeners = () => {
      const events = ['mousemove', 'touchstart'];
      
      events.forEach(eventType => {
        document.body.addEventListener(eventType, hotjarEventListener, { once: true });
        const cleanup = createEventCleanup(document.body, eventType, hotjarEventListener);
        listenersRef.current.push(cleanup);
      });
    };

    // Setup window scroll listener
    const setupScrollListener = () => {
      window.addEventListener('scroll', hotjarEventListener, { once: true });
      const cleanup = createEventCleanup(window, 'scroll', hotjarEventListener);
      listenersRef.current.push(cleanup);
    };

    // Setup all listeners
    setupBodyEventListeners();
    setupScrollListener();

    // Cleanup function
    return () => {
      listenersRef.current.forEach(cleanup => cleanup());
      listenersRef.current = [];
    };
  }, [authFrontLock]);
};

export default useHotjar; 